import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { recordReferral, storedReferralCode, clearReferralCookie } from "../lib/referral";
import { useRedirectTarget } from "../auth/redirect";
import { Button } from "../components/ui/Button";
import { Turnstile } from "../components/Turnstile";
import { getEnv } from "../config/env";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import { Logo } from "../components/Logo";
import { ThemeToggle } from "../components/ThemeToggle";

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const redirectTo = useRedirectTarget();
  // The referral code from the URL or the 3-month attribution cookie.
  const refCode = storedReferralCode();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [captcha, setCaptcha] = useState<string | null>(null);
  const needsCaptcha = Boolean(getEnv().VITE_TURNSTILE_SITE_KEY);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await register(email, password, captcha);
      // Best-effort: attribute this signup to a referral code if one was shared.
      if (refCode) { await recordReferral(refCode).catch(() => {}); clearReferralCookie(); }
      navigate(redirectTo);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create the account");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto grid min-h-full max-w-sm content-center gap-5 px-6 py-16">
      <div className="fixed right-4 top-4 z-10">
        <ThemeToggle />
      </div>
      <Logo size={44} className="mx-auto" />
      <h1 className="font-display text-2xl font-black tracking-tight">Create your account</h1>
      <p className="text-sm text-muted">
        One account, then create the profiles you need — driver, company, and more.
      </p>
      <form className="grid gap-3" onSubmit={onSubmit}>
        <label className="grid gap-1 text-xs font-bold text-muted">
          Email
          <input
            className="rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <label className="grid gap-1 text-xs font-bold text-muted">
          Password
          <input
            className="rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            required
          />
        </label>
        <Turnstile onToken={setCaptcha} />
        {error && <p className="font-mono text-sm text-danger">{error}</p>}
        <Button type="submit" disabled={busy || (needsCaptcha && !captcha)}>
          {busy ? "Creating…" : "Create account"}
        </Button>
      </form>
      <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-widest text-muted">
        <span className="h-px flex-1 bg-line" />
        or
        <span className="h-px flex-1 bg-line" />
      </div>
      <GoogleSignInButton label="Continue with Google" redirectTo={redirectTo} />
      <p className="text-sm text-muted">
        Already have an account?{" "}
        <Link className="font-bold text-accent-strong" to={`/login?redirect=${encodeURIComponent(redirectTo)}`}>
          Sign in
        </Link>
      </p>
    </div>
  );
}
